// MyLeaveAndTILContent.tsx
// Reusable content component for Leave & TIL (used in both standalone page and dashboard tab)

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Divider,
  Tabs,
  Tab,
  Stack,
  Paper,
} from '@mui/material';
import {
  BeachAccess as AnnualLeaveIcon,
  AccessTime as TimeIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axiosInstance from '../../utils/axiosConfig';

interface LeaveBalance {
  annualLeave: number;
  timeInLieu: number;
  timeInLieuDays: number;
  totalAvailableDays: number;
  entitlement?: {
    annualLeaveTotal: number;
    annualLeaveCurrentYear?: number;
    annualLeaveUsed: number;
    annualLeaveYear?: number;
    yearStartDate?: string;
    yearEndDate?: string;
  };
}

interface LeaveRequest {
  _id: string;
  type: 'annual-leave' | 'time-lieu';
  dateStart: string;
  dateEnd?: string;
  hoursRequested?: number;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  decisionNote?: string;
}

interface TILEntry {
  hours: number;
  earnedDate: string;
  expiryDate: string;
  description: string;
  status: 'expired' | 'expiring-soon' | 'active';
}

interface RotaLeaveDay { 
  date: string; 
  status: string; 
}

type StatusFilter = 'all' | 'approved' | 'pending' | 'rejected' | 'cancelled';

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString('en-IE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const formatWeekday = (date: string) =>
  new Date(date).toLocaleDateString('en-IE', { weekday: 'long' });

interface MyLeaveAndTILContentProps {
  personnelId: string;
}

const MyLeaveAndTILContent: React.FC<MyLeaveAndTILContentProps> = ({ personnelId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [tilEntries, setTilEntries] = useState<TILEntry[]>([]);
  const [rotaLeaveDays, setRotaLeaveDays] = useState<RotaLeaveDay[]>([]);

  // Tabs / Filters
  const [alStatus, setAlStatus] = useState<StatusFilter>('all');
  const [tilStatus, setTilStatus] = useState<StatusFilter>('all');

  useEffect(() => {
    if (!personnelId) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const [balanceRes, requestsRes, tilRes] = await Promise.all([
          axiosInstance.get(`/api/personnel/${personnelId}/leave/balance`),
          axiosInstance.get('/api/leave-requests/my-requests'),
          axiosInstance.get(`/api/personnel/${personnelId}/leave/til-history`),
        ]);

        const fullBalance: LeaveBalance = {
          ...balanceRes.data.balance,
          entitlement: balanceRes.data.entitlement,
        };
        setBalance(fullBalance);
        setLeaveRequests(requestsRes.data);
        setTilEntries(tilRes.data.tilEntries);

        const startISO = balanceRes.data.entitlement?.yearStartDate || `${new Date().getFullYear()}-01-01`;
        const endISO = balanceRes.data.entitlement?.yearEndDate || `${new Date().getFullYear()}-12-31`;
        const rotaRes = await axiosInstance.get(`/api/rota/person/${personnelId}/annual-leave-days?start=${startISO}&end=${endISO}`);
        setRotaLeaveDays(rotaRes.data.days || []);
      } catch (err: any) {
        console.error('Error loading leave data:', err);
        setError('Failed to load leave and TIL information.');
      } finally {
        setLoading(false);
      }
    })();
  }, [personnelId]);

  const yearStart = useMemo(() => {
    if (!balance?.entitlement?.yearStartDate) return new Date(new Date().getFullYear(), 0, 1);
    return new Date(balance.entitlement.yearStartDate);
  }, [balance]);

  const yearEnd = useMemo(() => {
    if (!balance?.entitlement?.yearEndDate) return new Date(new Date().getFullYear(), 11, 31);
    return new Date(balance.entitlement.yearEndDate);
  }, [balance]);

  const contractualEntitlement = balance?.entitlement?.annualLeaveTotal ?? 0;
  const currentYearAllocation = balance?.entitlement?.annualLeaveCurrentYear ?? contractualEntitlement;
  const usedAnnualLeave = balance?.entitlement?.annualLeaveUsed ?? 0;
  const remainingAnnualLeave = balance?.annualLeave ?? 0;
  const isProRata = currentYearAllocation !== contractualEntitlement;

  const totalTILEarnedThisYear = useMemo(() => {
    const start = yearStart.getTime();
    const end = yearEnd.getTime();
    return tilEntries.reduce((sum, e) => {
      const t = new Date(e.earnedDate).getTime();
      return t >= start && t <= end ? sum + e.hours : sum;
    }, 0);
  }, [tilEntries, yearStart, yearEnd]);

  const alRequests = useMemo(() => leaveRequests.filter(r => r.type === 'annual-leave'), [leaveRequests]);
  const tilRequestsOnly = useMemo(() => leaveRequests.filter(r => r.type === 'time-lieu'), [leaveRequests]);

  const applyStatusFilter = (items: LeaveRequest[], status: StatusFilter) =>
    status === 'all' ? items : items.filter(i => i.status === status);

  const alFiltered = applyStatusFilter(alRequests, alStatus);
  const tilFiltered = applyStatusFilter(tilRequestsOnly, tilStatus);

  const approvedALDaysThisYear = useMemo(() => {
    const daysFromReq: string[] = [];

    alRequests
      .filter(r => r.status === 'approved')
      .forEach(r => {
        const start = new Date(r.dateStart);
        const end = new Date(r.dateEnd || r.dateStart);
        const cur = new Date(start);
        cur.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        while (cur.getTime() <= end.getTime()) {
          if (cur >= yearStart && cur <= yearEnd) {
            daysFromReq.push(cur.toISOString());
          }
          cur.setDate(cur.getDate() + 1);
        }
      });

    const daysFromRota = rotaLeaveDays
      .map(d => new Date(d.date))
      .filter(d => d >= yearStart && d <= yearEnd)
      .map(d => new Date(d.setHours(0, 0, 0, 0)).toISOString());

    const merged = Array.from(new Set([...daysFromReq, ...daysFromRota]));
    merged.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return merged;
  }, [alRequests, rotaLeaveDays, yearStart, yearEnd]);

  const durationLabel = (req: LeaveRequest) => {
    if (req.hoursRequested) return `${req.hoursRequested}h`;
    const start = new Date(req.dateStart);
    const end = new Date(req.dateEnd || req.dateStart);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const currentYear = balance?.entitlement?.annualLeaveYear || new Date().getFullYear();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Leave & Time In Lieu
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your leave entitlement and balances for {currentYear} ({format(yearStart, 'dd MMM yyyy')} - {format(yearEnd, 'dd MMM yyyy')})
        </Typography>
      </Box>

      {/* Pro-Rata Notice */}
      {isProRata && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Pro-Rata Allocation:</strong> Your contractual entitlement is {contractualEntitlement} days per year, 
            but you have been allocated <strong>{currentYearAllocation} days for the remainder of {currentYear}</strong> based on your start date.
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      {balance && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <AnnualLeaveIcon color="primary" sx={{ fontSize: 48, mt: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      Annual Leave ({currentYear})
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ my: 1 }}>
                      {remainingAnnualLeave} days remaining
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Allocated (this year):</Typography>
                      <Typography variant="body2" fontWeight="bold">{currentYearAllocation} days</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Used:</Typography>
                      <Typography variant="body2" fontWeight="bold">{usedAnnualLeave} days</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Remaining:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">{remainingAnnualLeave} days</Typography>
                    </Box>
                    {isProRata && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'info.dark', borderRadius: 1 }}>
                        <Typography variant="caption" color="info.light">
                          ℹ️ Contractual entitlement: {contractualEntitlement} days/year
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <TimeIcon color="info" sx={{ fontSize: 48, mt: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Time in Lieu</Typography>
                    <Typography variant="h4" fontWeight="bold" color="info.main" sx={{ my: 1 }}>
                      {balance.timeInLieu}h available
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Earned this year:</Typography>
                      <Typography variant="body2" fontWeight="bold">{totalTILEarnedThisYear}h</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Available balance:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">{balance.timeInLieu}h</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Equivalent days:</Typography>
                      <Typography variant="body2" fontWeight="bold">~{balance.timeInLieuDays} days</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Annual Leave Requests */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Annual Leave Requests</Typography>
          <Tabs value={alStatus} onChange={(_, v) => setAlStatus(v)}>
            <Tab label="All" value="all" />
            <Tab label="Approved" value="approved" />
            <Tab label="Pending" value="pending" />
            <Tab label="Rejected" value="rejected" />
          </Tabs>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {alFiltered.length === 0 ? (
          <Alert severity="info">No annual leave requests for the selected filter.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date Start</TableCell>
                  <TableCell>Date End</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alFiltered.map((req) => (
                  <TableRow key={req._id}>
                    <TableCell>{format(new Date(req.dateStart), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{req.dateEnd ? format(new Date(req.dateEnd), 'dd/MM/yyyy') : '-'}</TableCell>
                    <TableCell>{durationLabel(req)}</TableCell>
                    <TableCell>
                      <Chip label={req.status} size="small" color={
                        req.status === 'approved' ? 'success' :
                        req.status === 'rejected' ? 'error' :
                        req.status === 'pending' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>{req.reason || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

{/* Approved Days */}
<Paper sx={{ p: 3, mb: 4 }}>
  <Typography variant="h6" gutterBottom>
    Approved Annual Leave Days — {currentYear}
  </Typography>
  <Divider sx={{ mb: 2 }} />
  {approvedALDaysThisYear.length === 0 ? (
    <Alert severity="info">No annual leave days recorded this year.</Alert>
  ) : (
    <>
      {(() => {
        // Group approved days by month/year
        const grouped: Record<string, string[]> = {};
        approvedALDaysThisYear.forEach(date => {
          const d = new Date(date);
          const key = format(d, 'MMMM yyyy');
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(date);
        });

        // Sort months chronologically
        const sortedMonths = Object.keys(grouped).sort((a, b) => {
          const aDate = new Date(a);
          const bDate = new Date(b);
          return aDate.getTime() - bDate.getTime();
        });

        return (
          <>
            {sortedMonths.map(month => (
              <Box key={month} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}
                >
                  {month} — {grouped[month].length} day
                  {grouped[month].length > 1 ? 's' : ''}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Weekday</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grouped[month]
                        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                        .map((date, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{formatDate(date)}</TableCell>
                            <TableCell>{formatWeekday(date)}</TableCell>
                          </TableRow>
                        ))}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold' }}>
                          Total: {grouped[month].length} day
                          {grouped[month].length > 1 ? 's' : ''}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
            <Divider sx={{ mt: 3, mb: 1 }} />
            <Typography variant="body2" align="right" sx={{ fontWeight: 'bold' }}>
              Year Total: {approvedALDaysThisYear.length} days
            </Typography>
          </>
        );
      })()}
    </>
  )}
</Paper>

      {/* TIL Requests */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Time In Lieu Requests</Typography>
          <Tabs value={tilStatus} onChange={(_, v) => setTilStatus(v)}>
            <Tab label="All" value="all" />
            <Tab label="Approved" value="approved" />
            <Tab label="Pending" value="pending" />
            <Tab label="Rejected" value="rejected" />
          </Tabs>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {tilFiltered.length === 0 ? (
          <Alert severity="info">No time in lieu requests for the selected filter.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tilFiltered.map((req) => (
                  <TableRow key={req._id}>
                    <TableCell>{format(new Date(req.dateStart), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{req.hoursRequested ? `${req.hoursRequested}h` : '-'}</TableCell>
                    <TableCell>
                      <Chip label={req.status} size="small" color={
                        req.status === 'approved' ? 'success' :
                        req.status === 'rejected' ? 'error' :
                        req.status === 'pending' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>{req.reason || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* TIL History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Time In Lieu History</Typography>
        <Divider sx={{ mb: 2 }} />
        {tilEntries.length === 0 ? (
          <Alert severity="info">No time in lieu entries recorded.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Earned</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tilEntries.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell>{format(new Date(entry.earnedDate), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{format(new Date(entry.expiryDate), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>{entry.hours}h</TableCell>
                    <TableCell>
                      <Chip label={entry.status.replace('-', ' ')} size="small" color={
                        entry.status === 'expired' ? 'error' : 
                        entry.status === 'expiring-soon' ? 'warning' : 'success'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default MyLeaveAndTILContent;